import { findingEntityEnum } from "../../Findings/FindingsUtils";
import type { FilterTreeNode } from "../AppViewUtils.types";
import {
  filterKeyEntityEnum,
  splitFilterKey,
  type FilterKeyEntity,
} from "./filterKey";
import { parseFilterQuery } from "./parseFilterQuery";

export const filterEntityEnum = {
  ...findingEntityEnum,
  RESOURCE: "RESOURCE",
} as const;

export type FilterEntity =
  (typeof filterEntityEnum)[keyof typeof filterEntityEnum];

export const findingFiltersDataContextEnum = {
  FINDINGS: "FINDINGS",
  RESOURCES: "RESOURCES",
  SILVER_FINDINGS: "SILVER_FINDINGS",
  REMEDIATION_ITEM: "REMEDIATION_ITEM",
  SEEMPLICITY_FINDING: "SEEMPLICITY_FINDING",
} as const;

export type FindingFiltersDataContext =
  (typeof findingFiltersDataContextEnum)[keyof typeof findingFiltersDataContextEnum];

const catalogContextByFilterEntity: Record<
  FilterEntity,
  FindingFiltersDataContext
> = {
  [filterEntityEnum.REMEDIATION_ITEM]: findingFiltersDataContextEnum.FINDINGS,
  [filterEntityEnum.SEEMPLICITY_FINDING]:
    findingFiltersDataContextEnum.FINDINGS,
  [filterEntityEnum.DATA_VALIDATION_FINDING]:
    findingFiltersDataContextEnum.SILVER_FINDINGS,
  [filterEntityEnum.RESOURCE]: findingFiltersDataContextEnum.RESOURCES,
};

export const toCatalogContext = (
  entity: FilterEntity
): FindingFiltersDataContext => catalogContextByFilterEntity[entity];

const splitCatalogContextByFilterEntity: Partial<
  Record<FilterEntity, FindingFiltersDataContext>
> = {
  [filterEntityEnum.REMEDIATION_ITEM]:
    findingFiltersDataContextEnum.REMEDIATION_ITEM,
  [filterEntityEnum.SEEMPLICITY_FINDING]:
    findingFiltersDataContextEnum.SEEMPLICITY_FINDING,
};

export const toSplitCatalogEntity = (
  entity: FilterEntity | null | undefined
): FilterEntity | undefined =>
  entity && splitCatalogContextByFilterEntity[entity] ? entity : undefined;

export const resolveCatalogContext = (
  entity: FilterEntity | null | undefined,
  isRemediationItemsSplit: boolean
): FindingFiltersDataContext | null => {
  if (entity === null) {
    return null;
  }

  if (entity === undefined) {
    return findingFiltersDataContextEnum.FINDINGS;
  }

  const splitCatalogContext =
    isRemediationItemsSplit || entity === filterEntityEnum.SEEMPLICITY_FINDING
      ? splitCatalogContextByFilterEntity[entity]
      : undefined;

  return splitCatalogContext ?? toCatalogContext(entity);
};

const legacyEntityKeyByFilterEntity: Record<FilterEntity, string> = {
  [filterEntityEnum.REMEDIATION_ITEM]: "FINDINGS",
  [filterEntityEnum.SEEMPLICITY_FINDING]: "SEEMPLICITY_FINDINGS",
  [filterEntityEnum.DATA_VALIDATION_FINDING]: "SILVER_FINDINGS",
  [filterEntityEnum.RESOURCE]: "RESOURCES",
};

export const toLegacyEntityKey = (entity: FilterEntity): string =>
  legacyEntityKeyByFilterEntity[entity];

export const UNSET_FILTER_ENTITY_KEY = "FINDING";

const hasFilterKeyEntity = (
  node: FilterTreeNode | null | undefined,
  entity: FilterKeyEntity
): boolean => {
  const field = node?.field;
  const operands = node?.operands;

  return (
    (typeof field === "string" && splitFilterKey(field).entity === entity) ||
    (Array.isArray(operands) &&
      operands.some((operand) => hasFilterKeyEntity(operand, entity)))
  );
};

export const getFilterKeysEntity = (
  filterJson: string | null | undefined
): FilterEntity | undefined => {
  const { filter } = parseFilterQuery(filterJson);

  if (hasFilterKeyEntity(filter, filterKeyEntityEnum.REMEDIATION_ITEM)) {
    return filterEntityEnum.REMEDIATION_ITEM;
  }

  return hasFilterKeyEntity(filter, filterKeyEntityEnum.FINDING)
    ? filterEntityEnum.SEEMPLICITY_FINDING
    : undefined;
};
